<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { DEFAULT_LOCALE, pathFor, type Locale } from "@/seo/routes-manifest";

const { t } = useI18n();
const route = useRoute();

const featuresPath = computed(() =>
  pathFor("features", (route.meta?.locale as Locale | undefined) ?? DEFAULT_LOCALE),
);

const features = computed(() => [
  {
    icon: "i-lucide-map",
    key: "routinePaths",
    hash: "#routine-paths",
    delay: "1",
  },
  {
    icon: "i-lucide-sparkles",
    key: "livingThemes",
    hash: "#living-themes",
    delay: "2",
  },
  {
    icon: "i-lucide-calendar-check",
    key: "eventsChores",
    hash: "#events-chores",
    delay: "3",
  },
  {
    icon: "i-lucide-bar-chart-3",
    key: "progress",
    hash: "#mood-week",
    delay: "4",
  },
]);
</script>

<template>
  <section id="features" class="bg-[var(--color-bg-secondary)] px-5 py-12 md:px-12 md:py-24">
    <h2
      data-reveal
      class="fade-up mx-auto mb-10 max-w-[1040px] text-center text-2xl font-medium text-[var(--color-text-primary)] md:mb-14 md:text-4xl"
      style="font-family: var(--font-display)"
    >
      {{ t("featureBlocks.heading") }}
    </h2>
    <div class="mx-auto grid max-w-[1040px] grid-cols-1 gap-6 md:grid-cols-2">
      <div
        v-for="feature in features"
        :key="feature.key"
        data-reveal
        :data-reveal-delay="feature.delay"
        class="fade-up rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-sm md:p-8"
      >
        <UIcon :name="feature.icon" class="size-12 text-[var(--color-primary)]" />
        <h3 class="mt-4 text-xl font-semibold text-[var(--color-text-primary)] md:text-[22px]">
          {{ t(`featureBlocks.${feature.key}.title`) }}
        </h3>
        <p class="mt-2 text-base leading-relaxed text-[var(--color-text-secondary)]">
          {{ t(`featureBlocks.${feature.key}.body`) }}
        </p>
        <router-link
          :to="{ path: featuresPath, hash: feature.hash }"
          class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--color-primary)] transition-colors hover:underline"
        >
          {{ t(`featureBlocks.${feature.key}.learnMoreLabel`) }}
          <UIcon name="i-lucide-arrow-right" class="size-3.5" />
        </router-link>
      </div>
    </div>
    <div class="mx-auto mt-8 max-w-[1040px] text-center">
      <router-link
        :to="featuresPath"
        data-reveal
        data-reveal-delay="5"
        class="fade-up inline-flex items-center gap-1.5 text-[15px] font-medium text-[var(--color-primary)] transition-colors hover:underline"
      >
        {{ t("featureBlocks.seeAll") }}
        <UIcon name="i-lucide-arrow-right" class="size-4" />
      </router-link>
    </div>
  </section>
</template>
